import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { formatWindow, formatTime } from '../utils/attendance';

// The trainee's details are remembered on their phone, so on day 2 and 3
// they only need to tap the button.
const REMEMBER_KEY = 'lp_attendance_identity';
const BLANK = { name: '', employee_id: '', email: '' };

const readRemembered = () => {
  try {
    return { ...BLANK, ...JSON.parse(localStorage.getItem(REMEMBER_KEY)) };
  } catch {
    return BLANK;
  }
};

const page = {
  minHeight: '100vh', background: '#f3f4f6', display: 'flex',
  alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', boxSizing: 'border-box'
};
const card = { width: '100%', maxWidth: '420px', padding: '24px' };

const MarkAttendance = () => {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(readRemembered);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/attendance/public/${token}`)
      .then(({ data }) => setSession(data))
      .catch(err => setLoadError(err.response?.data?.error || 'Could not load this attendance page. Check your connection and try again.'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post(`/attendance/public/${token}`, form);
      try { localStorage.setItem(REMEMBER_KEY, JSON.stringify(form)); } catch { /* private mode */ }
      setDone(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not mark attendance. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const header = (
    <div className="text-center mb-4">
      <img src="/lp-mark.png" alt="" style={{ height: '40px' }} />
      <h2 style={{ margin: '8px 0 4px' }}>{session?.title || 'Training Attendance'}</h2>
      {session && <p className="text-muted text-sm">{formatWindow(session.starts_at, session.ends_at)}</p>}
    </div>
  );

  if (loadError) {
    return <div style={page}><div className="card" style={card}>{header}<div className="alert alert-error">{loadError}</div></div></div>;
  }
  if (!session) {
    return <div style={page}><div className="text-center mt-5">Loading...</div></div>;
  }

  if (done) {
    return (
      <div style={page}>
        <div className="card text-center" style={card}>
          {header}
          <div style={{ fontSize: '56px', color: '#10b981', lineHeight: 1 }}>✓</div>
          <h2 style={{ margin: '12px 0 4px' }}>{done.already_marked ? 'Already marked present' : 'You are marked present'}</h2>
          <p className="text-muted">{done.name} · {formatTime(done.marked_at)}</p>
          <p className="text-muted text-sm mt-3">You can close this page.</p>
        </div>
      </div>
    );
  }

  if (session.status !== 'open') {
    return (
      <div style={page}>
        <div className="card" style={card}>
          {header}
          <div className="alert alert-info">
            {session.status === 'not_started'
              ? `Attendance opens at ${formatTime(session.starts_at)}. Please scan again then.`
              : 'Attendance for this session has closed. Please speak to your trainer.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <div className="card" style={card}>
        {header}
        <form onSubmit={submit}>
          <div className="form-group">
            <label>Your name</label>
            <input type="text" required maxLength={255} autoComplete="name"
              value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Employee ID</label>
            <input type="text" required maxLength={50} autoCapitalize="characters"
              value={form.employee_id} onChange={e => setForm({ ...form, employee_id: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" required maxLength={255} autoComplete="email" inputMode="email"
              value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          {error && <div className="alert alert-error mb-3">{error}</div>}
          <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={submitting}>
            {submitting ? 'Marking...' : 'Mark me present'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MarkAttendance;
